import { getAppointmentColor } from '../constants/colors.js';

// Date formatting utilities
const monthFormatter = new Intl.DateTimeFormat('es-ES', {
  month: 'long',
  year: 'numeric'
});

const weekRangeFormatter = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short'
});

export const formatMonthYear = (date) =>
  monthFormatter.format(date).replace(' de ', ' ').toUpperCase();

export const formatWeekRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return '';
  }

  const startLabel = weekRangeFormatter.format(startDate);
  const endLabel = weekRangeFormatter.format(endDate);

  return `${startLabel} - ${endLabel}`.replace(/\./g, '').toUpperCase();
};

// Formatea fecha local como YYYY-MM-DD.
// Evitamos toISOString porque puede correr la fecha por zona horaria.
export const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Date manipulation utilities
export const getStartOfWeek = (date) => {
  const start = new Date(date);
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;

  start.setDate(start.getDate() + diff);
  start.setHours(0, 0, 0, 0);

  return start;
};

export const isSameDay = (a, b) => (
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()
);

export const parseDateString = (dateString) => {
  if (!dateString) {
    return null;
  }

  const [year, month, day] = dateString.split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

export const isSameDayString = (dateString, dateObject) => {
  const parsedDate = parseDateString(dateString);

  if (!parsedDate || !dateObject) {
    return false;
  }

  return isSameDay(parsedDate, dateObject);
};

export const shiftDateByDays = (date, amount) => {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + amount);
  return next;
};

export const shiftDateByMonths = (date, amount) => {
  const next = new Date(date.getTime());
  const dayOfMonth = next.getDate();

  next.setDate(1);
  next.setMonth(next.getMonth() + amount);

  const lastDay = new Date(
    next.getFullYear(),
    next.getMonth() + 1,
    0
  ).getDate();

  next.setDate(Math.min(dayOfMonth, lastDay));

  return next;
};

// Calendar generation utilities
export const generateMonthDays = (referenceDate) => {
  const days = [];
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (firstDayOfMonth.getDay() + 6) % 7;

  for (let i = 0; i < startOffset; i += 1) {
    days.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    days.push(day);
  }

  return days;
};

// Función para obtener la proporción de píxeles por minuto según el tamaño de pantalla
export const getPixelsPerMinute = () => {
  if (typeof window === 'undefined') {
    return 1.111;
  }

  const width = window.innerWidth;

  if (width <= 768) {
    return 0.917;
  }

  return 1.111;
};

// Event positioning utilities
export const getEventPosition = (time) => {
  const hour = parseInt(time.split(':')[0], 10);
  const minute = parseInt(time.split(':')[1], 10);

  const pixelsPerMinute = getPixelsPerMinute();

  const totalMinutes = (hour - 6) * 60 + minute;

  return totalMinutes * pixelsPerMinute;
};

export const getEventHeight = (horaInicio, horaFin) => {
  const startHour = parseInt(horaInicio.split(':')[0], 10);
  const startMinute = parseInt(horaInicio.split(':')[1], 10);
  const endHour = parseInt(horaFin.split(':')[0], 10);
  const endMinute = parseInt(horaFin.split(':')[1], 10);

  const pixelsPerMinute = getPixelsPerMinute();

  const startTotalMinutes = (startHour - 6) * 60 + startMinute;
  const endTotalMinutes = (endHour - 6) * 60 + endMinute;

  const heightInMinutes = endTotalMinutes - startTotalMinutes;
  const heightInPixels = heightInMinutes * pixelsPerMinute;
  const adjustedHeight = heightInPixels - 16;

  return Math.max(1, adjustedHeight);
};

// Función para determinar el tipo de contenido según la duración del evento
export const getEventContentType = (horaInicio, horaFin) => {
  const startHour = parseInt(horaInicio.split(':')[0], 10);
  const startMinute = parseInt(horaInicio.split(':')[1], 10);
  const endHour = parseInt(horaFin.split(':')[0], 10);
  const endMinute = parseInt(horaFin.split(':')[1], 10);

  const startTotalMinutes = (startHour - 6) * 60 + startMinute;
  const endTotalMinutes = (endHour - 6) * 60 + endMinute;
  const durationInMinutes = endTotalMinutes - startTotalMinutes;

  if (durationInMinutes <= 25) {
    return 'compact';
  }

  return 'normal';
};

// Función para obtener el contenido del evento según su tipo
export const getEventDisplayContent = (appointment, contentType) => {
  const pacienteNombre = appointment.paciente_nombre_completo ||
    `${appointment.paciente_nombre || ''} ${appointment.paciente_apellido || ''}`.trim() ||
    'Paciente sin nombre';

  const nameParts = pacienteNombre.trim().split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  switch (contentType) {
    case 'compact':
      return {
        fullName:
          pacienteNombre.length > 20
            ? `${pacienteNombre.substring(0, 20)}...`
            : pacienteNombre,
        fontSize: '10px',
        showEndTime: false,
        isCompact: true
      };

    case 'normal':
    default:
      return {
        firstName,
        lastName,
        fontSize: '11px',
        showEndTime: true,
        isCompact: false
      };
  }
};

// Utilities for appointments
export const formatAppointmentTime = (horaInicio, horaFin) => {
  return `${horaInicio} ${horaFin}`;
};

export const formatAppointmentPrice = (valorSesion) => {
  const value = Number(valorSesion) || 0;
  return `$${value.toLocaleString()}`;
};

export const getAppointmentStatusColor = (estado, isPast = false) => {
  return getAppointmentColor(estado, isPast);
};

export const getAppointmentStatusLabel = (estado) => {
  const statusLabels = {
    PENDIENTE: 'Pendiente',
    REALIZADO: 'Realizado',
    CANCELADO: 'Cancelado',
    NO_ASISTIO: 'No asistió'
  };

  return statusLabels[estado] || estado;
};

export const isOnlineAppointment = (linkReunion) => {
  return Boolean(linkReunion);
};

export const getAppointmentsForDate = (citas, fecha) => {
  if (!Array.isArray(citas) || !fecha) {
    return [];
  }

  const targetDate = formatDateLocal(fecha);

  return citas.filter((cita) => {
    return cita.fecha_turno === targetDate;
  });
};

export const getAppointmentsForWeek = (citas, startDate, endDate) => {
  if (!Array.isArray(citas) || !startDate || !endDate) {
    return [];
  }

  const start = formatDateLocal(startDate);
  const end = formatDateLocal(endDate);

  return citas.filter((cita) => {
    return cita.fecha_turno >= start && cita.fecha_turno <= end;
  });
};

export const sortAppointmentsByTime = (appointments) => {
  if (!Array.isArray(appointments)) {
    return [];
  }

  return [...appointments].sort((a, b) => {
    return a.hora_inicio.localeCompare(b.hora_inicio);
  });
};

export const getAppointmentDayOfWeek = (fechaTurno) => {
  const date = parseDateString(fechaTurno);

  if (!date) {
    return null;
  }

  return (date.getDay() + 6) % 7;
};

export const generateDayTimeSlots = (timeSlots, appointments) => {
  if (!Array.isArray(appointments)) {
    return [];
  }

  const sortedAppointments = [...appointments].sort((a, b) => {
    return a.hora_inicio.localeCompare(b.hora_inicio);
  });

  return sortedAppointments.map((appointment) => {
    return {
      time: appointment.hora_inicio,
      appointment,
      isEmpty: false
    };
  });
};

export const getCurrentTime = () => {
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
};

export const getCurrentTimePosition = () => {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();

  const pixelsPerMinute = getPixelsPerMinute();

  const totalMinutes = (hours - 6) * 60 + minutes;

  return totalMinutes * pixelsPerMinute;
};

// Función para calcular la posición del indicador de hora actual en la vista por día
export const getCurrentTimeIndicatorPosition = (timeSlots, currentTime) => {
  let closestSlotIndex = -1;
  let minDifference = Infinity;

  timeSlots.forEach((slot, index) => {
    const slotTime = slot.time;
    const [slotHour, slotMinute] = slotTime.split(':').map(Number);
    const [currentHour, currentMinute] = currentTime.split(':').map(Number);

    const slotTotalMinutes = slotHour * 60 + slotMinute;
    const currentTotalMinutes = currentHour * 60 + currentMinute;

    const difference = Math.abs(slotTotalMinutes - currentTotalMinutes);

    if (difference < minDifference) {
      minDifference = difference;
      closestSlotIndex = index;
    }
  });

  return closestSlotIndex;
};

export const formatCurrentTime = () => {
  const now = new Date();

  return now.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

export const getCurrentDayOfWeek = () => {
  const now = new Date();

  return (now.getDay() + 6) % 7;
};

export const isCurrentDay = (date) => {
  const today = new Date();

  return isSameDay(date, today);
};

export const getCenteredScrollPosition = (currentTimePosition, containerHeight) => {
  const centeredPosition = currentTimePosition - containerHeight / 2;

  return Math.max(0, centeredPosition);
};

export const isEventInPast = (appointment) => {
  if (!appointment?.fecha_turno) {
    return false;
  }

  const now = new Date();
  const today = formatDateLocal(now);

  if (appointment.fecha_turno < today) {
    return true;
  }

  if (appointment.fecha_turno === today) {
    const currentTime = getCurrentTime();
    return appointment.hora_fin < currentTime;
  }

  return false;
};

export const isEventInProgress = (appointment) => {
  if (!appointment?.fecha_turno) {
    return false;
  }

  const now = new Date();
  const today = formatDateLocal(now);

  if (appointment.fecha_turno !== today) {
    return false;
  }

  const currentTime = getCurrentTime();

  return appointment.hora_inicio <= currentTime && currentTime < appointment.hora_fin;
};

export const isTimeSlotInPast = (timeSlot, selectedDate) => {
  const now = new Date();
  const today = formatDateLocal(now);
  const selectedDateString = formatDateLocal(selectedDate);

  if (selectedDateString < today) {
    return true;
  }

  if (selectedDateString === today) {
    const currentTime = getCurrentTime();
    return timeSlot < currentTime;
  }

  return false;
};

// Agrupa los turnos del día según el estado de notificación.
// Esto nos sirve para la vista Mes, donde mostramos 4 indicadores
// resumidos en las esquinas de cada celda.
export const getNotificationSummaryForAppointments = (appointments = []) => {
  const summary = {
    sinNotificacion: 0,
    problema: 0,
    atencion: 0,
    ok: 0
  };

  appointments.forEach((appointment) => {
    const estado = appointment?.estado_notificacion || 'SIN_NOTIFICACION';

    // Sin notificación configurada.
    if (estado === 'SIN_NOTIFICACION') {
      summary.sinNotificacion += 1;
      return;
    }

    // Problemas graves.
    if (
      estado === 'REBOTADO' ||
      estado === 'SPAM' ||
      estado === 'ERROR'
    ) {
      summary.problema += 1;
      return;
    }

    // Estados de advertencia / seguimiento.
    if (
      estado === 'DEMORADO' ||
      estado === 'REINTENTO_PENDIENTE'
    ) {
      summary.atencion += 1;
      return;
    }

    // Todo lo demás lo consideramos dentro del grupo "ok".
    // Incluye: PROGRAMADA, ENVIADA, ENTREGADO
    summary.ok += 1;
  });

  return summary;
};